import React, { useCallback, useEffect, useState } from 'react';
import { Card, Button, Input } from '../../../components/ui';
import { Wallet, RefreshCw, Plus, CheckCircle, AlertTriangle } from 'lucide-react';
import { fetchAPI } from '../../../services/apiClient';

// SUPERVISOR — Registro de abonos.
//
// Esta pantalla NO muestra saldos, ganancias ni consolidados financieros
// de la banca (regla AGENTS.md §1.1 / prompt PROMPT_AJUSTE_SALDO_*).
// Solo permite registrar abonos por local/cuenta y consultar el historial
// mínimo de los abonos creados por el SUPERVISOR autenticado.

interface LocalRef {
  id: string;
  nombre: string;
}

interface AbonoMio {
  abono_id: string;
  local_id: string;
  local_nombre: string;
  monto: string;
  fecha: string | null;
  referencia: string | null;
  created_at: string | null;
}

interface RegistroResp {
  ok: boolean;
  message: string;
  abono_id: string;
  monto: string;
  fecha: string;
  local_nombre?: string;
}

const formatMoney = (v: string | number) =>
  `₡${Number(v || 0).toLocaleString('es-CR', { minimumFractionDigits: 0 })}`;

export const SupervisorAbonosPage: React.FC = () => {
  const [locales, setLocales] = useState<LocalRef[]>([]);
  const [historial, setHistorial] = useState<AbonoMio[]>([]);
  const [localId, setLocalId] = useState<string>('');
  const [monto, setMonto] = useState<string>('');
  const [observacion, setObservacion] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    setErrMsg(null);
    try {
      // Lista mínima de locales de la banca (no expone saldos: solo id+nombre).
      const locs = await fetchAPI<Array<{ id: string; nombre: string }>>(
        '/listas/locales',
      ).catch(() => [] as Array<{ id: string; nombre: string }>);
      setLocales(locs.map(l => ({ id: l.id, nombre: l.nombre })));

      const mios = await fetchAPI<AbonoMio[]>(
        '/saldos/abonos-supervisor/mios?limit=20',
      ).catch(() => [] as AbonoMio[]);
      setHistorial(mios);
    } catch (e: any) {
      setErrMsg(e?.message || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    setOkMsg(null);
    setErrMsg(null);

    if (!localId) {
      setErrMsg('Seleccione un local.');
      return;
    }
    const montoNum = Number(monto);
    if (!monto || Number.isNaN(montoNum) || montoNum <= 0) {
      setErrMsg('Ingrese un monto válido mayor a 0.');
      return;
    }

    setSubmitting(true);
    try {
      const resp = await fetchAPI<RegistroResp>('/saldos/abono-supervisor', {
        method: 'POST',
        body: JSON.stringify({
          local_id: localId,
          monto: montoNum,
          observacion: observacion.trim() || undefined,
        }),
      });
      setOkMsg(
        `${resp.message}. Monto ${formatMoney(resp.monto)} registrado el ${resp.fecha}.`,
      );
      // Limpiar formulario (sin mostrar saldo).
      setLocalId('');
      setMonto('');
      setObservacion('');
      // Refrescar historial mínimo propio.
      cargar();
    } catch (e: any) {
      setErrMsg(e?.message || 'No se pudo registrar el abono.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Wallet size={24} /> Registrar abono
          </h1>
          <p className="text-gray-500 text-sm">
            Registre pagos recibidos del local hacia la banca. Esta pantalla no
            muestra saldos ni totales financieros.
          </p>
        </div>
        <button
          onClick={cargar}
          className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
          title="Refrescar"
        >
          <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      <Card>
        <form onSubmit={guardar} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Local / Cuenta
            </label>
            <select
              value={localId}
              onChange={e => setLocalId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              disabled={submitting}
              required
            >
              <option value="">— Seleccione —</option>
              {locales.map(l => (
                <option key={l.id} value={l.id}>{l.nombre}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Monto (₡)
            </label>
            <Input
              type="number"
              min="1"
              step="1"
              value={monto}
              onChange={e => setMonto(e.target.value)}
              placeholder="0"
              disabled={submitting}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Observación (opcional)
            </label>
            <Input
              type="text"
              value={observacion}
              onChange={e => setObservacion(e.target.value)}
              placeholder="Referencia, nota, # comprobante…"
              disabled={submitting}
              maxLength={280}
            />
          </div>

          {okMsg && (
            <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
              <CheckCircle size={18} className="shrink-0 mt-0.5" />
              <span>{okMsg}</span>
            </div>
          )}
          {errMsg && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
              <AlertTriangle size={18} className="shrink-0 mt-0.5" />
              <span>{errMsg}</span>
            </div>
          )}

          <div className="flex justify-end">
            <Button type="submit" disabled={submitting}>
              <Plus size={16} className="mr-1" />
              {submitting ? 'Registrando…' : 'Registrar abono'}
            </Button>
          </div>
        </form>
      </Card>

      <Card>
        <h2 className="font-semibold text-gray-800 mb-3">
          Mis abonos recientes
        </h2>
        {historial.length === 0 ? (
          <p className="text-sm text-gray-500">
            No has registrado abonos todavía.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-600 border-b">
                  <th className="py-2 px-2">Fecha</th>
                  <th className="py-2 px-2">Local</th>
                  <th className="py-2 px-2 text-right">Monto</th>
                  <th className="py-2 px-2">Observación</th>
                </tr>
              </thead>
              <tbody>
                {historial.map(a => (
                  <tr key={a.abono_id} className="border-b last:border-0">
                    <td className="py-2 px-2 text-gray-700">{a.fecha || '—'}</td>
                    <td className="py-2 px-2 text-gray-800">{a.local_nombre}</td>
                    <td className="py-2 px-2 text-right font-medium text-gray-900">
                      {formatMoney(a.monto)}
                    </td>
                    <td className="py-2 px-2 text-gray-600">
                      {a.referencia || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default SupervisorAbonosPage;
